import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * IdleWarningModal — "are you still there?" prompt shown shortly before the
 * idle signout (CJIS §5.4). Gives the user a chance to acknowledge presence
 * and keep the session instead of being kicked out cold. Passive activity
 * does NOT dismiss it (AuthContext freezes the idle clock while it's up) —
 * the user must click Stay signed in; when the countdown hits zero the
 * signout proceeds.
 *
 * Portaled to <body> so a transformed ancestor can't clip the fixed overlay.
 */
export default function IdleWarningModal({ deadline, onStay, onExpire }) {
  const [remainMs, setRemainMs] = useState(() => Math.max(0, deadline - Date.now()));
  const expiredRef = useRef(false);

  useEffect(() => {
    const tick = () => {
      const remain = Math.max(0, deadline - Date.now());
      setRemainMs(remain);
      if (remain <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpire();
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deadline, onExpire]);

  const totalSec = Math.ceil(remainMs / 1000);
  const countdown = `${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, '0')}`;

  // Put the countdown in the tab title too: the warning usually goes up
  // while the person is looking at another tab or window, where a modal in
  // this one is invisible.
  useEffect(() => {
    const original = document.title;
    return () => { document.title = original; };
  }, []);
  useEffect(() => {
    document.title = `(${countdown}) Signing out — still there?`;
  }, [countdown]);

  return createPortal((
    <div className="np-export-overlay">
      <div className="np-export-modal" style={{ maxWidth: 440 }}>
        <div className="np-export-head">
          <h3>Are you still there?</h3>
        </div>
        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <p style={{ marginTop: 0, fontSize: 13, color: '#555' }}>
            You’ve been inactive for a while. For your security, you’ll be
            signed out in <b>{countdown}</b>.
          </p>
          <p style={{ marginBottom: 0, fontSize: 13, color: '#555' }}>
            Signing back in will require your password and verification code.
          </p>
        </div>
        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onStay} autoFocus>
            I’m still here — stay signed in
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
